/**
 * Authorization — master spec §2.
 *
 * The tenant defines FIVE app roles; this system has THREE permission tiers.
 * That is deliberate and is not a shape to be "fixed": the five strings exist
 * so Entra group membership can mirror how the sales org describes itself, and
 * collapsing them here is the whole of the mapping. Adding a fourth tier is a
 * spec change, not an implementation detail.
 *
 * This module is imported by BOTH sides. The client hides UI with the same
 * function the server enforces with, so the two cannot drift.
 */

import { z } from 'zod';

// --------------------------------------------------------------------------
// App roles
// --------------------------------------------------------------------------

/**
 * The `roles` claim carries an app role's `value` field, not its display name,
 * and matching is CASE-SENSITIVE (§8d, "five things to verify"). These strings
 * are what we expect Menlo's manifests to carry; item 4 of the outstanding
 * follow-up in ITRequest.md is IT confirming them verbatim. If they come back
 * as e.g. `Funnel.Rep`, this array is the single place that changes.
 */
export const APP_ROLES = ['Rep', 'FAE', 'SalesManager', 'Exec', 'Admin'] as const;
export type AppRole = (typeof APP_ROLES)[number];

export const appRoleSchema = z.enum(APP_ROLES);

// --------------------------------------------------------------------------
// Tiers
// --------------------------------------------------------------------------

export const TIERS = ['User', 'Manager', 'Admin'] as const;
export type Tier = (typeof TIERS)[number];

export const tierSchema = z.enum(TIERS);

/** Higher number wins when a user carries several roles. */
const TIER_RANK: Record<Tier, number> = { User: 0, Manager: 1, Admin: 2 };

/**
 * The §2 table, and the whole of the mapping.
 *
 * Note what is NOT here:
 *  - `FAE` grants no special write access by itself. An FAE writes the deals
 *    they are assigned to via `opportunities.fae_user_id`, which is data, not
 *    a role. An FAE assigned to nothing writes nothing but their own notes.
 *  - `Exec` is read-only in practice, not by enforcement. An Exec who owns no
 *    opportunities can write nothing but their own notes, which is the intent,
 *    but the model does not PREVENT an Exec being made an owner. Enforcing
 *    that is a fourth tier and a spec change (§2).
 */
export const APP_ROLE_TO_TIER: Record<AppRole, Tier> = {
  Rep: 'User',
  FAE: 'User',
  Exec: 'User',
  SalesManager: 'Manager',
  Admin: 'Admin',
};

export function isAppRole(value: string): value is AppRole {
  return (APP_ROLES as readonly string[]).includes(value);
}

/**
 * Resolve a `roles` claim to a tier. Unknown role strings are ignored rather
 * than treated as errors (§8d) — the tenant may grow labels this system does
 * not model. Zero RECOGNIZED roles returns null, which the caller turns into
 * `403 NO_ROLE_ASSIGNED` and the "access not provisioned" screen.
 *
 * The tier is the HIGHEST one present, so `Rep` + `SalesManager` is Manager.
 */
export function resolveTier(roles: readonly string[] | null | undefined): Tier | null {
  let best: Tier | null = null;
  for (const role of roles ?? []) {
    if (!isAppRole(role)) continue;
    const tier = APP_ROLE_TO_TIER[role];
    if (best === null || TIER_RANK[tier] > TIER_RANK[best]) best = tier;
  }
  return best;
}

/** The recognized subset of a `roles` claim, order preserved, deduplicated. */
export function knownRoles(roles: readonly string[] | null | undefined): AppRole[] {
  const seen = new Set<AppRole>();
  for (const role of roles ?? []) {
    if (isAppRole(role)) seen.add(role);
  }
  return [...seen];
}

// --------------------------------------------------------------------------
// The one scope predicate
// --------------------------------------------------------------------------

/** The subject of an authorization question. */
export interface Actor {
  userId: string;
  tier: Tier;
}

/** The minimum of an opportunity needed to answer the write question. */
export interface OpportunityOwnership {
  owner_user_id: string | null;
  fae_user_id: string | null;
}

/**
 * §2: since all tiers read everything, authorization work is entirely in
 * writes, and it reduces to one question asked of one function.
 *
 * The `fae_user_id` branch [R2] is load-bearing: an FAE who ran the design
 * review must be able to log the meeting, add the technical note, and close
 * the action item on that deal.
 *
 * It creates no escalation route. Setting `fae_user_id` requires already being
 * able to write the opportunity, so nobody can assign themselves onto a deal
 * they cannot already touch — and `owner_user_id` / `fae_user_id` are settable
 * only by Manager or Admin, which is enforced at the route, not here.
 */
export function canWriteOpportunity(actor: Actor, opp: OpportunityOwnership): boolean {
  return (
    actor.tier === 'Admin' ||
    actor.tier === 'Manager' ||
    (opp.owner_user_id !== null && opp.owner_user_id === actor.userId) ||
    (opp.fae_user_id !== null && opp.fae_user_id === actor.userId)
  );
}

/**
 * Setting a deal aside as **Invalid — not a real opportunity**, or bringing it
 * back: Manager or Admin only (docs/bugs.md session 3, decision 12).
 */
export function canSetInvalid(actor: Actor): boolean {
  return actor.tier === 'Manager' || actor.tier === 'Admin';
}

/**
 * May this actor move this opportunity from its current status to `toStatus`?
 *
 * Any transition that touches `invalid` — closing as Invalid, reopening an
 * Invalid deal, re-closing it as something else, or winning it through a stage
 * move — needs `canSetInvalid` on top of the ordinary write rule. Every other
 * transition is `canWriteOpportunity` alone.
 *
 * The API refuses with it and the web hides the controls with it, so a button
 * a User sees is one the server accepts. Neither side checks this on its own.
 */
export function canChangeOpportunityStatus(
  actor: Actor,
  opp: OpportunityOwnership & { status: string },
  toStatus: string,
): boolean {
  if (!canWriteOpportunity(actor, opp)) return false;
  if (opp.status === 'invalid' || toStatus === 'invalid') return canSetInvalid(actor);
  return true;
}

/**
 * Ticking or unticking **Focus** or **Verified** on a deal: Manager or Admin
 * only (docs/bugs.md session 4, decisions 14/15). Everyone sees and filters on
 * the flags; a User sees the boxes disabled.
 *
 * Deliberately not tied to `canWriteOpportunity`: an owner or assigned FAE who
 * may edit everything else on the deal still may not vouch for it. The API
 * refuses a write carrying either field with this, and the web disables the
 * boxes with it. Every user today is Menlo staff; when outside users arrive,
 * this is the one place that changes.
 */
export function canSetOpportunityFlags(actor: Actor): boolean {
  return actor.tier === 'Manager' || actor.tier === 'Admin';
}

/** Reference data (§2): `rep_firms`, `products`, `competitors`, `stages`, `reason_codes`, `app_settings`. */
export function canWriteReferenceData(actor: Actor): boolean {
  return actor.tier === 'Admin';
}

/** Reassignment, transfer, and any endpoint that writes opportunities it did not name individually (§2 rules 2 and 3). */
export function canReassign(actor: Actor): boolean {
  return actor.tier === 'Manager' || actor.tier === 'Admin';
}

// --------------------------------------------------------------------------
// Account strategy — the first READ restriction in the app
// --------------------------------------------------------------------------

/**
 * The caller's relationship to one account, **computed server-side** and
 * returned in the strategy payload so the client asks the same two predicates
 * below without a second query.
 *
 *  - `isAccountOwner`: `accounts.owner_user_id` is the caller.
 *  - `onTeam`: the caller is the account owner, OR the `owner_user_id` or
 *    `fae_user_id` of ANY non-deleted opportunity on the account, whatever its
 *    status — a closed or lost deal still counts here.
 *
 * `created_by`, `updated_by` and `reviewed_by` do NOT count: they record who
 * touched a row, and the importer would otherwise be on every account.
 */
export interface AccountTeamMembership {
  isAccountOwner: boolean;
  onTeam: boolean;
}

/**
 * **Read** a strategy: Manager or Admin, or anyone on the account team.
 *
 * Everywhere else in this app everyone reads everything (§2). Strategy content
 * — the stored sections, strategic next steps and strategy images — is the one
 * exception, and it is enforced at instance level in the handlers, never in the
 * permission matrix, whose `read` rows all stay `ALL`.
 */
export function canReadAccountStrategy(actor: Actor, membership: AccountTeamMembership): boolean {
  return (
    actor.tier === 'Admin' ||
    actor.tier === 'Manager' ||
    membership.isAccountOwner ||
    membership.onTeam
  );
}

/**
 * **Write** a strategy (sections 1, 3, 6, 7, 8 and both images): Manager or
 * Admin, or the ACCOUNT OWNER. An FAE or opportunity owner who is not the
 * account owner reads but does not write — except the one narrow status rule
 * in `canAssigneeUpdateStrategicStep`.
 */
export function canWriteAccountStrategy(actor: Actor, membership: AccountTeamMembership): boolean {
  return actor.tier === 'Admin' || actor.tier === 'Manager' || membership.isAccountOwner;
}

/**
 * What the assigned internal owner of a strategic step may change, and nothing
 * else: its status, its completion time, its completion note. Not the title,
 * the date, the owner, or anything else.
 */
export const STRATEGIC_STEP_ASSIGNEE_FIELDS = [
  'status',
  'completed_at',
  'completion_note',
] as const;

/**
 * The narrow exception to the strategy write rule (user decision, 2026-09-22):
 * the step's assigned internal owner may mark their own step's progress.
 *
 * `fields` are the columns the write would change. An empty change is refused
 * rather than waved through, so this can never be the reason a no-op write
 * reaches a row the caller could not otherwise touch.
 */
export function canAssigneeUpdateStrategicStep(
  actor: Actor,
  step: { owner_user_id: string | null },
  fields: readonly string[],
): boolean {
  if (step.owner_user_id === null || step.owner_user_id !== actor.userId) return false;
  if (fields.length === 0) return false;
  return fields.every((field) =>
    (STRATEGIC_STEP_ASSIGNEE_FIELDS as readonly string[]).includes(field),
  );
}

/** A strategy writer changes anything on a step; otherwise the assignee rule decides. */
export function canUpdateStrategicStep(
  actor: Actor,
  membership: AccountTeamMembership,
  step: { owner_user_id: string | null },
  fields: readonly string[],
): boolean {
  return (
    canWriteAccountStrategy(actor, membership) ||
    canAssigneeUpdateStrategicStep(actor, step, fields)
  );
}
